import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Kensington and Chelsea\'s RED DfT rating guarantee my claim will succeed?',
    answer: 'No rating guarantees success. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. A RED overall and condition scorecard is useful context — the Department for Transport classifies the borough\'s published road condition evidence as inadequate — but your claim still needs photos, repair invoices, and ideally prior reports of the same defect.',
  },
  {
    question: 'The council says its standards are "amongst the highest in the country" — does that block my claim?',
    answer: 'Not automatically. The council\'s website makes that claim, but its own 2025 transparency report describes a gradual decline in A-road condition (green-rated carriageway falling from 64% in 2022 to 62% in 2024 while amber rose to 34%). The DfT rates the borough RED on condition and flags incomplete road condition data. Marketing language and a claimant\'s evidence about a specific pothole are separate questions.',
  },
  {
    question: 'Why is spend AMBER when condition is RED?',
    answer: 'The DfT spend scorecard compares capital investment against the authority\'s allocation — not whether every road is well maintained. Kensington and Chelsea projects £328,000 capital spend in 2025/26 against a £328,000 DfT allocation, with an 89:11 preventative-to-reactive split in its transparency report. That earns AMBER on spend. Overall and condition ratings remain RED because published condition evidence — limited to 16.7km of A-roads — does not support a green scorecard.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 154.5km — 82% of the borough\'s 188.3km carriageway network. The council conducts SCANNER laser surveys only on A roads (16.7km). B, C and U roads are assessed visually by engineers, with no published red/amber/green percentages. If your incident was on a residential street, the council\'s published condition data may not cover your road at all.',
  },
  {
    question: 'The DfT says K&C\'s ratings use incomplete condition data — what does that mean?',
    answer: 'Kensington and Chelsea carries the same DfT footnote as several London boroughs: overall, condition and best-practice scorecards are based on incomplete road condition data. The council itself states SCANNER surveys are not carried out on B, C or U roads due to budget constraints. That structural gap limits what network-level scorecards can prove about your specific street.',
  },
  {
    question: 'The council spent £0 on capital maintenance for three years — is that still relevant?',
    answer: 'It can be. The council\'s maintaining-roads page confirms zero DfT grant and zero capital investment in 2019–20, 2020–21 and 2021–22, with capital spend resuming at £101,000 in 2023–24 and £721,000 planned for 2024–25. Whether that history affects your claim depends on when and where the defect formed, and what inspection records the council can produce for that location.',
  },
  {
    question: 'How do I report a pothole to Kensington and Chelsea?',
    answer: 'Report carriageway defects via the council\'s online form at rbkc.gov.uk, or contact Streetline on 020 7361 3001 or highways@rbkc.gov.uk. Prior reports of the same defect can demonstrate the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function KensingtonAndChelseaCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Kensington and Chelsea Pothole Claims | RED DfT Rating, 82% of Roads Without SCANNER Data | Fixtyer"
        description="Royal Borough of Kensington and Chelsea: RED DfT condition rating, zero capital spend for three years, and 154.5km of unclassified roads with no published SCANNER condition data. What that means for your Section 58 claim."
        canonical="/councils/kensington-and-chelsea"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Kensington and Chelsea', url: '/councils/kensington-and-chelsea' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Royal Borough of Kensington and Chelsea"
        councilSlug="kensington-and-chelsea"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/kensington-and-chelsea"
        headline="Kensington and Chelsea Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Kensington and Chelsea</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-rose-200 text-sm">
              <strong className="text-rose-400">DfT caveat:</strong> The Department for Transport flags that
              Kensington and Chelsea's 2025/26 overall, condition and best-practice scorecards are based on{' '}
              <strong className="text-white">incomplete road condition data</strong>.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            154.5km of Kensington Roads Have No Published SCANNER Condition Data
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            The Royal Borough of Kensington and Chelsea maintains 188.3km of carriageway — one of London's
            smallest networks, but among its most heavily used. The DfT rates the borough{' '}
            <strong className="text-rose-400">RED</strong> on overall and condition scorecards. Just{' '}
            <strong className="text-white">16.7km of A roads</strong> carry published SCANNER surveys; the other{' '}
            <strong className="text-rose-400">171.6km of B, C and U roads (91%)</strong> are assessed visually only.
            The council confirms <strong className="text-amber-400">£0 capital investment for three consecutive years</strong>{' '}
            before HS2 reallocated funding arrived. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">RED</div>
            <div className="text-white font-medium mt-2">DfT condition scorecard 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              A-road green-rated carriageway fell from 64% (2022) to 62% (2024) while amber rose to 34%. Reactive
              maintenance cost approximately £450,000 in 2024/25 — potholes, trip hazards and emergency call-outs.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            188.3km of Dense Urban Carriageway
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from the council's 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">188.3km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">154.5km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">82% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">17.1km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-700">16.7km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">A roads (SCANNER only)</div>
              <div className="text-xs text-emerald-700 mt-1">8.9% of carriageway</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Kensington and Chelsea highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">376.6km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway structures (bridges, embankment wall)</td>
                  <td className="px-6 py-4 text-center font-medium">6</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">8,856</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">10,500</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">0.8km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Live-source caveat:</strong> The council's roads and pavements page states that maps may not
              be available following a cyber-attack and may not reflect current conditions. Condition survey data
              in the transparency report is also a snapshot — the council notes it "does not represent the condition
              throughout the year" and is affected by seasonal weather and utility works.
            </p>
          </div>

          <QuoteCard
            quote="Many of our road network is under increasing strain, as many carriageways weren't designed to handle today's heavier vehicles, such as EVs and buses, or the constant pressure of 24/7 traffic. This accelerates deterioration and shortens asset life."
            source="Royal Borough of Kensington and Chelsea Local Highways Maintenance Transparency Report (2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Published Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys on A roads only — B, C and U roads assessed visually with no published RAG percentages
          </p>

          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 mb-8">
            <p className="text-rose-800 text-sm">
              <strong>Methodology caveat:</strong> The council conducts SCANNER surveys only on A roads. B, C and
              unclassified roads are assessed visually by engineers "due to budget constraints." No red, amber or
              green percentages are published for those 171.6km. The DfT's incomplete-data flag reflects this gap.
            </p>
          </div>

          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (16.7km) — SCANNER condition 2022–2024</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.65%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.88%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3.3%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">34%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">62%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              The council describes a "gradual decline in overall carriageway condition, with a shift towards more
              roads requiring intervention" — green-rated A-road carriageway falling while amber rises. Data for
              2020 and 2021 is unavailable due to COVID-19 disruptions.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">171.6km of B, C and U roads — no published RAG data</h3>
            <p className="text-amber-800 text-sm mb-3">
              Residential and estate streets across Notting Hill, Earl's Court, Chelsea and Kensington make up the
              bulk of the network. The council uses visual inspections and public reports to prioritise defects, but
              does not publish SCANNER-style condition breakdowns for these roads.
            </p>
            <p className="text-amber-800 text-sm">
              The council inspects principal roads monthly and minor roads every six months. Urgent repairs are made
              within 2 or 24 hours depending on severity — but a six-month inspection cycle on residential streets
              leaves gaps a specific, evidence-led claim can explore.
            </p>
          </div>

          <QuoteCard
            quote="We conduct SCANNER surveys only on A roads. The condition of B, C, and unclassified (U) roads is assessed visually by our experienced engineers, and this information is used to develop the annual maintenance work programme. SCANNER surveys are not carried out on these roads due to budget constraints."
            source="Royal Borough of Kensington and Chelsea Local Highways Maintenance Transparency Report (2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            AMBER spend scorecard — but three years of zero capital investment before HS2 funding
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Kensington and Chelsea highway maintenance spending 2020–2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">328</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">328</td>
                  <td className="px-4 py-3 text-center">4,149</td>
                  <td className="px-4 py-3 text-center text-emerald-400">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">101</td>
                  <td className="px-4 py-3 text-center">101</td>
                  <td className="px-4 py-3 text-center">4,048</td>
                  <td className="px-4 py-3 text-center">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">101</td>
                  <td className="px-4 py-3 text-center">101</td>
                  <td className="px-4 py-3 text-center">3,738</td>
                  <td className="px-4 py-3 text-center">91%</td>
                  <td className="px-4 py-3 text-center">9%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">0</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">0</td>
                  <td className="px-4 py-3 text-center">3,876</td>
                  <td className="px-4 py-3 text-center">91%</td>
                  <td className="px-4 py-3 text-center">9%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">0</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">0</td>
                  <td className="px-4 py-3 text-center">3,861</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">0</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">0</td>
                  <td className="px-4 py-3 text-center">3,861</td>
                  <td className="px-4 py-3 text-center">90%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-brand-400 text-xs mb-8">
            Table figures exclude drainage, street lighting and structure maintenance costs. The council's
            maintaining-roads page publishes wider revenue totals (e.g. £6.2m for 2024/25) covering the full
            local highway network including those assets.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Three years of zero capital</h3>
              <p className="text-brand-200 text-sm">
                The council confirms zero DfT grant and zero capital investment in 2019–20 through 2021–22, with
                capital resuming at £101,000 in 2023–24. London's HS2 reallocated funding provides £101,000 annually
                for 2023–24 and 2024–25, with £3.156m total over 11 years — including planned work on Albert Bridge
                and Kensington Road sections.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">£450,000 reactive in 2024/25</h3>
              <p className="text-brand-200 text-sm">
                Despite a 89:11 preventative-to-reactive split in the transparency report, total reactive maintenance
                expenditure in 2024/25 was approximately £450,000 — covering emergency call-outs, potholes,
                trip hazards on footways and safety-related reinstatements. Reactive workload persists even where
                preventative share is high.
              </p>
            </div>
          </div>

          <div className="bg-white/10 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <caption className="sr-only">Kensington and Chelsea wider capital investment from maintaining-roads page</caption>
              <thead>
                <tr className="border-b border-white/20">
                  <th className="px-6 py-4 text-left font-semibold text-brand-200">Year</th>
                  <th className="px-6 py-4 text-right font-semibold text-brand-200">DfT grant</th>
                  <th className="px-6 py-4 text-right font-semibold text-brand-200">Total capital investment</th>
                  <th className="px-6 py-4 text-right font-semibold text-brand-200">Revenue (wider scope)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                <tr className="bg-rose-500/20">
                  <td className="px-6 py-4 font-medium">2019–2020</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right">£5.5m</td>
                </tr>
                <tr className="bg-rose-500/20">
                  <td className="px-6 py-4 font-medium">2020–2021</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right">£5.5m</td>
                </tr>
                <tr className="bg-rose-500/20">
                  <td className="px-6 py-4 font-medium">2021–2022</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right">£5.5m</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">2022–2023</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right text-rose-300 font-bold">£0</td>
                  <td className="px-6 py-4 text-right">£5.53m</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">2023–2024</td>
                  <td className="px-6 py-4 text-right text-emerald-300">£101k</td>
                  <td className="px-6 py-4 text-right text-emerald-300">£101k</td>
                  <td className="px-6 py-4 text-right">£5.6m</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">2024–2025</td>
                  <td className="px-6 py-4 text-right text-emerald-300">£101k</td>
                  <td className="px-6 py-4 text-right text-emerald-300">£721k*</td>
                  <td className="px-6 py-4 text-right">£6.2m*</td>
                </tr>
              </tbody>
            </table>
            <p className="px-6 py-3 text-brand-400 text-xs">*Subject to Key Decision approval — council maintaining-roads page</p>
          </div>
        </div>
      </section>

      {/* Marketing vs data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Council Claims vs Published Data
          </h2>
          <p className="text-surface-600 mb-8">
            What the council says publicly — and what its transparency report and the DfT record
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-4">What the council claims</h3>
              <p className="text-emerald-800 italic mb-4">
                "Our standard of highways maintenance and street lighting are amongst the highest in the country."
              </p>
              <p className="text-emerald-700 text-sm mb-3">— Maintaining roads and pavements page</p>
              <p className="text-emerald-800 text-sm">
                The council resurfaces 50–80 roads annually, plans a 90:10 preventative-to-reactive split for 2025/26,
                and is exploring AI-driven defect detection.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What the data shows</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li>• DfT RED overall and condition scorecards for 2025/26</li>
                <li>• DfT flags incomplete road condition data</li>
                <li>• Zero capital investment 2019–20 to 2021–22</li>
                <li>• A-road green condition falling; amber rising (2022–2024)</li>
                <li>• 171.6km of B/C/U roads with no published SCANNER RAG data</li>
                <li>• ~£450,000 reactive spend in 2024/25</li>
              </ul>
            </div>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="Our approach to highway maintenance is to carry out the optimum amount of planned maintenance to minimise the need for more expensive reactive repairs."
              source="Royal Borough of Kensington and Chelsea Local Highways Maintenance Transparency Report (2025)"
            />
            <QuoteCard
              quote="We are investigating new ways to manage and assess the condition of our highway through AI, continuing to roll out new low energy street lighting infrastructure."
              source="Royal Borough of Kensington and Chelsea — Maintaining roads and pavements (May 2026)"
            />
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How the council says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual SCANNER laser surveys (16.7km)</li>
                <li>• <strong>B, C and U roads:</strong> visual assessment only — no published RAG data</li>
                <li>• <strong>Principal roads and heavily used pavements:</strong> inspected monthly</li>
                <li>• <strong>Minor roads:</strong> inspected every six months</li>
                <li>• <strong>Urgent repairs:</strong> within 2 or 24 hours depending on severity</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Utility works and wear</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council coordinates street works via LondonWorks, applies Section 58 restrictions after major
                resurfacing, and holds monthly contractor meetings. It notes ongoing concerns about reinstatement
                quality and that utility trenches affect road lifespan.
              </p>
              <p className="text-surface-600 text-sm">
                Some routes are maintained by Transport for London (red-line roads). Claims on TfL-managed highways
                go to TfL, not the borough.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Kensington and Chelsea
              must maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              a preventative maintenance strategy or resurface 50–80 roads a year.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the six-month minor-road inspection cycle — and had it been inspected recently?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal, Streetline) giving actual notice?</li>
              <li>• On a U-road, can the council produce condition records beyond visual assessment notes?</li>
            </ul>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER spend scorecard — capital aligned with DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 89–91% preventative share in recent transparency data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented monthly/six-monthly inspection programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 50–80 roads resurfaced annually; HS2 funding for bridge work
                </li>
              </ul>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall and condition DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT incomplete condition data flag
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 154.5km U-roads with no published SCANNER RAG breakdown
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Three years of zero capital investment before 2023
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road condition trending from green towards amber
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits EV/bus weight and utility works accelerate wear
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Kensington and Chelsea
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.rbkc.gov.uk/report-problem/report-problem-roads-and-pavements"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a roads or pavements problem — rbkc.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Or contact Streetline: 020 7361 3001 · highways@rbkc.gov.uk
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Kensington and Chelsea?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated borough with incomplete condition data demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road SCANNER data-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No DfT incomplete-data citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 154.5km U-road evidence gap documented</li>
                <li>✅ RED DfT scorecards and incomplete-data caveat cited</li>
                <li>✅ Zero-capital history and reactive spend referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Kensington and Chelsea</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=kensington-and-chelsea"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Kensington and Chelsea Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.rbkc.gov.uk/sites/default/files/media/documents/Dft%20-%20RBKC_Highway_Transparency_Report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Royal Borough of Kensington and Chelsea Local Highways Maintenance Transparency Report (2025)
            </a>{' '}
            |{' '}
            <a
              href="https://www.rbkc.gov.uk/streets-and-transport/roads-and-pavements/maintaining-roads-and-pavements"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Maintaining roads and pavements (May 2026)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            . Last verified: June 2026.
          </p>
        </div>
      </section>
    </div>
  );
}
